import React, { useEffect, useRef } from 'react';
import { DialogV2 } from '@withjoy/joykit';
import type { ApolloError } from '@apollo/client';
import { Router } from './routes';
import type { GetCheckoutDialogDataQuery } from './CheckoutDialog.graphql.generated';
import { useCheckoutFunnel } from './hooks/useCheckoutFunnel';
import { CheckoutFunnelProvider } from './CheckoutFunnelProvider';
import { CheckoutInitDataProvider, EMPTY_CHECKOUT_INIT_DATA, useGetCheckoutInitData } from './hooks/useCheckoutInitData';
import type { CheckoutDialogProps } from './CheckoutDialog.types';
import { useCheckoutDialogContext } from './CheckoutDialog.context';
import { addError } from '@shared/utils/logger';

type UseInitMachineArgs = {
  data: GetCheckoutDialogDataQuery | undefined;
  error: ApolloError | undefined;
  registryItemId: string;
  isAffiliate: boolean;
  isGiftWrapPurchased: boolean;
};

const useInitMachine = ({ data, error, registryItemId, isAffiliate, isGiftWrapPurchased }: UseInitMachineArgs) => {
  const { route, setupMachine } = useCheckoutFunnel(ctx => [ctx.route]);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current || route !== 'setup') {
      return;
    }

    if (!data && !error) {
      return;
    }

    if (error) {
      addError(error, { context: 'CheckoutDialog.initQuery', registryItemId, isAffiliate });
    }

    const { registryItem, order, orderMetadata, registryOrder } = data ?? {};
    const existingOrder = isAffiliate ? registryOrder : order;

    setupMachine({
      isGiftWrapPurchased,
      registryItem,
      // @ts-expect-error the auto-generated `SetupEvent` existingOrder type requires platformType, which registryOrder does not have
      existingOrder: existingOrder && orderMetadata ? { ...existingOrder, isPurchased: orderMetadata.isPurchased } : null,
    });
    hasInitializedRef.current = true;
  }, [route, data, error, registryItemId, setupMachine, isAffiliate, isGiftWrapPurchased]);
};

type DialogInternalProps = {
  registryItemId: string;
  currentOrderId: Maybe<string>;
  isAffiliate: boolean;
  isGiftWrapPurchased: boolean;
};
/**
 * When `Dialog`s are not open, its children are completely unmounted.
 * This separate internal component allows us to safely initialize/tear down the state machine.
 */
const DialogInternal = (props: DialogInternalProps) => {
  const { data, error } = useGetCheckoutInitData(props);
  useInitMachine({ ...props, data, error });

  return (
    <CheckoutInitDataProvider value={data ?? EMPTY_CHECKOUT_INIT_DATA}>
      <DialogV2.Body>
        <Router />
      </DialogV2.Body>
    </CheckoutInitDataProvider>
  );
};

export const CheckoutDialog = (props: CheckoutDialogProps) => {
  const { isOpen, onClose, registryItemId, currentOrderId, isAffiliate, isGroupGifting, isGiftWrapPurchased } = props;
  const { disableCloseOnOutsideClick } = useCheckoutDialogContext();

  return (
    <>
      <DialogV2
        isOpen={isOpen}
        onClose={onClose}
        size={isAffiliate || isGroupGifting ? '3xl' : '2xl'}
        overrides={{ Body: { props: { paddingX: 0, paddingY: 0 } } }}
        disableCloseOnOutsideClick={disableCloseOnOutsideClick}
      >
        <CheckoutFunnelProvider {...props}>
          {/* Dialog content is unmounted completely if  `isOpen=false`, so we'll check for
         `registryItemId` when evaluating `isOpen` higher up in the comp hierarchy  */}
          <DialogInternal
            registryItemId={registryItemId!}
            currentOrderId={currentOrderId}
            isAffiliate={isAffiliate}
            isGiftWrapPurchased={isGiftWrapPurchased}
          />
        </CheckoutFunnelProvider>
      </DialogV2>
    </>
  );
};
